import {
  GENERIC_PROFILE_SLUG,
  gameProfileSchema,
  okSchema,
  type GameProfile,
} from '@hearth/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import { Copy, Gamepad2, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { Confirm } from '@/components/confirm';
import { GameIcon } from '@/components/game-icon';
import { DuplicateProfileDialog } from '@/components/game-profile-editor';
import { PageHeader } from '@/components/page-header';
import { QueryState } from '@/components/query-state';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { apiRequest } from '@/lib/api';
import { gameProfilesQuery } from '@/lib/admin-queries';
import { errorMessage } from '@/lib/forms';

export const Route = createFileRoute('/_app/admin/game-profiles/')({
  component: GameProfilesPage,
});

function GameProfilesPage() {
  const query = useQuery(gameProfilesQuery);
  const queryClient = useQueryClient();
  const [duplicating, setDuplicating] = useState<GameProfile | null>(null);
  const invalidate = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: ['admin', 'game-profiles'] }),
      queryClient.invalidateQueries({ queryKey: ['admin', 'eggs'] }),
    ]);
  const toggle = useMutation({
    mutationFn: ({ id, enabled }: { id: number; enabled: boolean }) =>
      apiRequest(`/api/application/game-profiles/${id}`, {
        method: 'PATCH',
        body: { enabled },
        schema: gameProfileSchema,
      }),
    onSuccess: invalidate,
    onError: (e) => toast.error(errorMessage(e)),
  });
  const remove = useMutation({
    mutationFn: (id: number) =>
      apiRequest(`/api/application/game-profiles/${id}`, { method: 'DELETE', schema: okSchema }),
    onSuccess: invalidate,
    onError: (e) => toast.error(errorMessage(e)),
  });

  return (
    <>
      <PageHeader
        title="Game profiles"
        icon={Gamepad2}
        description="How Hearth presents each game: its look, which tabs servers get and which eggs it covers."
        actions={
          <Button asChild>
            <Link to="/admin/game-profiles/new">
              <Plus /> New profile
            </Link>
          </Button>
        }
      />
      <QueryState query={query}>
        {(profiles) => (
          <div className="table-card">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Profile</TableHead>
                  <TableHead>Source</TableHead>
                  <TableHead className="text-right">Priority</TableHead>
                  <TableHead className="text-right">Eggs</TableHead>
                  <TableHead>Enabled</TableHead>
                  <TableHead className="w-24" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {profiles.map((p) => {
                  const generic = p.slug === GENERIC_PROFILE_SLUG;
                  return (
                    <TableRow key={p.id}>
                      <TableCell>
                        <Link
                          to="/admin/game-profiles/$profileId"
                          params={{ profileId: String(p.id) }}
                          className="flex items-center gap-3"
                        >
                          <GameIcon identity={p.definition.identity} size="sm" />
                          <span className="min-w-0">
                            <span className="block font-medium hover:underline">{p.name}</span>
                            <span className="text-muted-foreground block font-mono text-xs">
                              {p.slug}
                            </span>
                          </span>
                        </Link>
                      </TableCell>
                      <TableCell>
                        {p.source === 'builtin' ? (
                          <Badge variant="secondary">Built-in</Badge>
                        ) : (
                          <Badge variant="info">Custom</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {generic ? 'Fallback' : p.priority}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">{p.eggCount}</TableCell>
                      <TableCell>
                        <Switch
                          checked={p.enabled}
                          disabled={generic || toggle.isPending}
                          aria-label={`${p.enabled ? 'Disable' : 'Enable'} ${p.name}`}
                          title={generic ? 'The fallback for every egg; always on.' : undefined}
                          onCheckedChange={(enabled) => toggle.mutate({ id: p.id, enabled })}
                        />
                      </TableCell>
                      <TableCell className="text-right whitespace-nowrap">
                        <Button
                          variant="ghost"
                          size="icon"
                          aria-label={`Duplicate ${p.name}`}
                          title="Duplicate"
                          onClick={() => setDuplicating(p)}
                        >
                          <Copy />
                        </Button>
                        {p.source === 'custom' && (
                          <Confirm
                            title={`Delete ${p.name}?`}
                            description="Eggs that match it go back to the other profiles. Profiles pinned to an egg can't be deleted."
                            action="Delete"
                            destructive
                            onConfirm={() => remove.mutate(p.id)}
                          >
                            <Button variant="ghost" size="icon" aria-label={`Delete ${p.name}`}>
                              <Trash2 />
                            </Button>
                          </Confirm>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </QueryState>
      {duplicating && (
        <DuplicateProfileDialog profile={duplicating} onClose={() => setDuplicating(null)} />
      )}
    </>
  );
}
